import { ChangeEvent, FC, useCallback, useEffect, useState } from 'react'
import { Button } from '@progress/kendo-react-buttons'
import {
  Card,
  CardBody,
  CardHeader,
  CardSubtitle,
  CardTitle,
  GridLayout,
  GridLayoutItem
} from '@progress/kendo-react-layout'
import { Typography } from '@progress/kendo-react-common'
import { Input, TextArea, TextAreaChangeEvent } from '@progress/kendo-react-inputs'
import { MultiLineBody } from './MultiLineBody'
import { ConfirmReplyActions, CommonActions, useAppDispatch, useAppSelector } from '../../stores'
import { ErrorMessage, ErrorMessages } from './ErrorMessages'
import { ConfirmDialog } from './ConfirmDialog'
import { HearingReplyItemSummaryGateway } from '../../gateway/supplier/HearingReplyItemSummaryGateway'
import { PutSupplierHearingStatusItemGateway } from '../../gateway/supplier/PutSupplierHearingStatusItemGateway'
import { BinaryStorageContentDispositionType, BlobFileService, MessageProvider } from '../../services'
import { GetSupplierHearingReplyItemSummaryRes } from '@gateway.Supplier'
import dayjs from 'dayjs'
import { Authority } from '../../gateway/system/Authority'

interface SupplierHearingHeaderProps {
  regStatus?: string
  promptInfo?: string
  enableFixContentRegistration?: boolean
  supplierHeaderInfo?: {
    replyYm: string
    supplierNm: string
    expense: string
    branchNm: string
    factoryNm: string
    supplierBranchNm: string
  }
}

export const getFixContentControlState = (
  replySts: string | undefined,
  authority: number | undefined,
  lastMonthFlg: boolean,
  isNotOwnBranch: boolean
) => ({
  fixContentDisabled:
    replySts === '確認済' ||
    authority === Authority.Supplier ||
    lastMonthFlg ||
    isNotOwnBranch,
  downloadRestricted: lastMonthFlg || isNotOwnBranch
})

export const SupplierHearingHeader: FC<SupplierHearingHeaderProps> = (
  props
) => {
  const dispatch = useAppDispatch()
  const { headerInfo } = useAppSelector((state) => state.confirmreply)
  const {
    simulationYm,
    supplierCd,
    supplierBranchCd,
    factoryCd,
    expenseCd,
    branchCd
  } = useAppSelector((state) => state.confirmreply.confirmReplyInfo)
  const displayStartYear = useAppSelector((state) => state.common.displayStartYear)
  const userProp = useAppSelector((state) => state.common.userProp)
  const [supplierHearing, setSupplierHearing] = useState<
  GetSupplierHearingReplyItemSummaryRes['supplierHearing']
  >()
  const [attachmentFile, setAttachmentFile] = useState<File>()
  const [attachmentFileName, setAttachmentFileName] = useState<string>()
  const [uploadedAttachmentFilePath, setUploadedAttachmentFilePath] = useState<string>()
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false)
  const [notificationMessages, setNotificationMessages] = useState<ErrorMessage[]>([])
  const [errorMessages, setErrorMessages] = useState<ErrorMessage[]>([])

  const fetchSupplierHearing = useCallback(() => {
    if (props.enableFixContentRegistration !== true) {
      return
    }

    dispatch(CommonActions.setIsLoading(true))
    HearingReplyItemSummaryGateway.getSupplierHearingReplyItemSummary(
      simulationYm,
      supplierCd,
      supplierBranchCd,
      factoryCd,
      expenseCd,
      0,
      displayStartYear
    )
      .then((res) => {
        if (!res.error) {
          setSupplierHearing(res.data.supplierHearing)
          dispatch(ConfirmReplyActions.setHeaderInfo({
            replyComment: res.data.supplierHearing.replyComment,
            replyNmLast: res.data.supplierHearing.replyNmLast,
            replyNmFirst: res.data.supplierHearing.replyNmFirst,
            fixContent: res.data.supplierHearing.fixContent,
            attachmentFilePath: res.data.supplierHearing.attachmentFilePath
          }))
        } else if (Array.isArray(res.messages) && res.messages.length > 0) {
          setErrorMessages(res.messages.map<ErrorMessage>((message) => ({
            id: message.id,
            content: message.message
          })))
        }
      })
      .finally(() => {
        dispatch(CommonActions.setIsLoading(false))
      })
  }, [dispatch, displayStartYear, expenseCd, factoryCd, props.enableFixContentRegistration, simulationYm, supplierBranchCd, supplierCd])

  useEffect(() => {
    fetchSupplierHearing()
  }, [fetchSupplierHearing])

  const changeFixContent = (event: TextAreaChangeEvent) => {
    setSupplierHearing((current) => current === undefined
      ? current
      : { ...current, fixContent: event.value })
  }

  const handleUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file === undefined) {
      return
    }

    const extension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase()
    if (extension !== '.xls' && extension !== '.xlsx') {
      setErrorMessages([{
        id: 'E010',
        content: MessageProvider.provide('E010', '「.xls」または「.xlsx」')
      }])
      return
    }

    const blob = file.slice(0, file.size, file.type)
    const uploadFileName = `${dayjs().format('YYYYMMDDhhmmss')}_${file.name}`
    const uploadFile = new File([blob], uploadFileName, { type: file.type })
    setAttachmentFile(uploadFile)
    setAttachmentFileName(file.name)
    setUploadedAttachmentFilePath(undefined)
  }

  const handleDownload = () => {
    const attachmentFilePath = supplierHearing?.attachmentFilePath
    if (attachmentFilePath === undefined || attachmentFilePath === null || attachmentFilePath === '') {
      return
    }

    new BlobFileService()
      .download('attached-files', attachmentFilePath)
      .then((res) => {
        BlobFileService.downloadRedirectTo(res)
      })
      .finally(() => {})
  }

  const handleRegister = async () => {
    setIsConfirmDialogOpen(false)
    if (supplierHearing === undefined) {
      return
    }

    dispatch(CommonActions.setIsLoading(true))
    try {
      let attachmentFilePath = uploadedAttachmentFilePath ?? supplierHearing.attachmentFilePath ?? ''
      if (attachmentFile !== undefined && uploadedAttachmentFilePath === undefined) {
        await new BlobFileService().upload(
          'attached-files',
          attachmentFile,
          {
            dispositionType: BinaryStorageContentDispositionType.Attachment,
            filenameAlias: attachmentFileName
          }
        )
        attachmentFilePath = attachmentFile.name
        setUploadedAttachmentFilePath(attachmentFilePath)
      }

      const res = await PutSupplierHearingStatusItemGateway.putHearingStatusItem({
        data: {
          supplierHearingList: [{
            simulationYm,
            expenseCd,
            supplierCd,
            supplierBranchCd,
            factoryCd,
            replyComment: supplierHearing.replyComment ?? '',
            replyNmLast: supplierHearing.replyNmLast ?? '',
            replyNmFirst: supplierHearing.replyNmFirst ?? '',
            fixContent: supplierHearing.fixContent ?? '',
            attachmentFilePath,
            updDtm: supplierHearing.updDtm
          }],
          replyStsCdAfterChange: supplierHearing.replyStsCd,
          takeBackFlg: 0,
          takeOverFlg: 0,
          fixContentFlg: 1
        }
      })

      if (!res.error) {
        setAttachmentFile(undefined)
        setAttachmentFileName(undefined)
        setUploadedAttachmentFilePath(undefined)
        setNotificationMessages([{
          id: 'I004',
          content: MessageProvider.provide('I004', '修正内容登録')
        }])
        fetchSupplierHearing()
      } else if (Array.isArray(res.messages) && res.messages.length > 0) {
        setErrorMessages(res.messages.map<ErrorMessage>((message) => ({
          id: message.id,
          content: message.message
        })))
      }
    } catch {
      setErrorMessages([{
        id: 'E001',
        content: '添付資料のアップロードに失敗しました。'
      }])
    } finally {
      dispatch(CommonActions.setIsLoading(false))
    }
  }

  const supplierHeaderInfo = supplierHearing ?? headerInfo
  const attachmentFilePath = supplierHeaderInfo.attachmentFilePath ?? ''
  const displayedAttachmentFileName = attachmentFileName ??
    (attachmentFilePath === '' ? undefined : attachmentFilePath.substring(attachmentFilePath.indexOf('_') + 1))
  const currentDate = new Date()
  const simulatedYear = Number(simulationYm.substring(0, 4))
  const simulatedMonth = Number(simulationYm.substring(4))
  const lastMonthFlg =
    simulatedYear < currentDate.getFullYear() ||
    (simulatedYear === currentDate.getFullYear() && simulatedMonth < currentDate.getMonth() + 1)
  const isNotOwnBranch = userProp?.authority === Authority.Branch && userProp.branchCode !== branchCd
  const fixContentControlState = getFixContentControlState(
    supplierHearing?.replySts,
    userProp?.authority,
    lastMonthFlg,
    isNotOwnBranch
  )

  return (
    <>
      <Card orientation="horizontal">
        <CardHeader>
        <CardTitle>
          <span
            style={{ fontSize: '1.2em', fontWeight: 'bold', color: 'gray' }}
          >
            {props.supplierHeaderInfo?.replyYm}
          </span>
        </CardTitle>
        <CardSubtitle>
          <span
            style={{ fontSize: '1.2em', fontWeight: 'bold', color: 'gray' }}
          >
            {props.regStatus === '1'
              ? '未登録'
              : props.regStatus === '2'
                ? '一時保存'
                : props.regStatus === '3'
                  ? '登録済'
                  : props.regStatus === '4'
                    ? '未登録（事前調整）'
                    : ''}
          </span>
        </CardSubtitle>
        </CardHeader>
        <CardBody>
        <GridLayout gap={{ rows: 5, cols: 0 }}>
          <GridLayoutItem col={1} row={1}>
            <span style={{ fontWeight: 'bold', fontSize: '1.2em' }}>
              {props.supplierHeaderInfo?.branchNm}
            </span>
          </GridLayoutItem>
          <GridLayoutItem col={1} row={2}>
            <span style={{ fontWeight: 'bold', fontSize: '1.2em' }}>
              {props.supplierHeaderInfo?.expense}
            </span>
          </GridLayoutItem>
          <GridLayoutItem col={1} row={3}>
            <span style={{ fontWeight: 'bold', fontSize: '1.2em' }}>
              {props.supplierHeaderInfo?.supplierNm}
              {' '}{props.supplierHeaderInfo?.supplierBranchNm}
              {props.supplierHeaderInfo?.factoryNm !== undefined
                ? `${props.supplierHeaderInfo.factoryNm}`
                : ''}
            </span>
          </GridLayoutItem>

          <GridLayoutItem col={2} row={1} rowSpan={4}>
            <TextArea
              rows={4}
              placeholder={'回答コメントを入力して下さい。'}
              style={{ width: '20vw' }}
              readOnly={true}
              value={supplierHeaderInfo.replyComment ?? undefined}
            />
          </GridLayoutItem>
          <GridLayoutItem col={2} row={5}>
            <Input
              style={{ width: '10vw' }}
              placeholder={'回答者(姓)'}
              disabled={true}
              value={supplierHeaderInfo.replyNmLast ?? undefined}
            />
            <Input
              style={{ width: '10vw' }}
              placeholder={'回答者(名)'}
              disabled={true}
              value={supplierHeaderInfo.replyNmFirst ?? undefined}
            />
          </GridLayoutItem>

          <GridLayoutItem col={3} row={1} rowSpan={4}>
            <TextArea
              rows={4}
              placeholder={'修正内容を入力して下さい。'}
              style={{ width: '20vw' }}
              readOnly={props.enableFixContentRegistration !== true || fixContentControlState.fixContentDisabled}
              value={supplierHeaderInfo.fixContent ?? undefined}
              onChange={props.enableFixContentRegistration === true ? changeFixContent : undefined}
            />
          </GridLayoutItem>

          {props.enableFixContentRegistration === true && (
            <GridLayoutItem col={3} row={5}>
              <Input
                style={{ width: '15vw' }}
                disabled
                placeholder={'添付資料を選択して下さい'}
                value={displayedAttachmentFileName}
              />
              <Button
                style={{ marginLeft: '5px' }}
                icon="upload"
                disabled={fixContentControlState.fixContentDisabled}
              />
              <Button
                style={{ marginLeft: '5px' }}
                icon="download"
                themeColor="primary"
                disabled={attachmentFilePath === '' || attachmentFile !== undefined || fixContentControlState.downloadRestricted}
                onClick={handleDownload}
              />
              <input
                style={{
                  marginLeft: '5px',
                  width: '25px',
                  opacity: '0',
                  position: 'relative',
                  left: '-66px'
                }}
                disabled={fixContentControlState.fixContentDisabled}
                title=""
                type="file"
                onChange={handleUpload}
                accept="application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              />
              <Button
                style={{ marginLeft: '5px', minWidth: '120px' }}
                themeColor="primary"
                disabled={fixContentControlState.fixContentDisabled || supplierHearing === undefined}
                onClick={() => setIsConfirmDialogOpen(true)}
              >
                修正内容登録
              </Button>
            </GridLayoutItem>
          )}

          <GridLayoutItem col={4} rowSpan={3}>
            <Typography.p
              fontWeight={'bold'}
              themeColor={'error'}
              fontSize={'large'}
            >
              <MultiLineBody body={props.promptInfo} />
            </Typography.p>
          </GridLayoutItem>
          </GridLayout>
        </CardBody>
      </Card>
      <ConfirmDialog
        title="登録"
        open={isConfirmDialogOpen}
        renderCloseButton
        id="I007"
        args={['修正内容登録']}
        onClickOk={() => { void handleRegister() }}
        onClickCancel={() => setIsConfirmDialogOpen(false)}
      />
      <ErrorMessages type="notification" messages={notificationMessages} />
      <ErrorMessages type="dialog" messages={errorMessages} />
    </>
  )
}
